import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceLine } from "recharts";
import { Activity, Users, UserPlus, PlusSquare, Megaphone, Clock, MousePointerClick, Eye, Radio, Search, Monitor, MapPin, Repeat } from "lucide-react";
import { CountTile, GatedPill, SectionTitle, BarList, ExportButton, EmptyNote, CollectingNote, secondsToDuration } from "./shared";

const RANGES = [["today", "Today"], ["7d", "Last 7 days"], ["30d", "Last 30 days"], ["custom", "Custom"]];

function StatNote({ label, value, note }) {
  return (
    <div className="border border-sniper bg-surface px-3 py-2">
      <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-heading text-lg text-foreground leading-tight mt-0.5">{value}</p>
      {note && <p className="font-mono text-[10px] text-muted-foreground mt-0.5 leading-snug">{note}</p>}
    </div>
  );
}

export default function TrafficTab({ force }) {
  const [range, setRange] = useState("30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [includeStaff, setIncludeStaff] = useState(false);
  const [data, setData] = useState(null);
  const [rt, setRt] = useState(null);
  const [search, setSearch] = useState(null);

  const qs = () => {
    let q = `range=${range}`;
    if (range === "custom" && from && to) q += `&from=${from}&to=${to}`;
    if (includeStaff) q += `&include_staff=1`;
    return q + (force ? "&_force_refresh=1" : "");
  };

  useEffect(() => {
    if (range === "custom" && (!from || !to)) return;
    api.get(`/admin/analytics/traffic?${qs()}`).then(({ data }) => setData(data)).catch(() => {});
    api.get(`/admin/analytics/site-search?${qs()}`).then(({ data }) => setSearch(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, from, to, force, includeStaff]);

  // Real-time — poll every 10s, but skip while the tab is hidden and refresh on re-focus.
  useEffect(() => {
    let alive = true;
    const poll = () => {
      if (document.hidden) return;
      api.get("/admin/analytics/traffic-realtime").then(({ data }) => alive && setRt(data)).catch(() => {});
    };
    poll();
    const t = setInterval(poll, 10000);
    const onVisible = () => { if (!document.hidden) poll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  const chartData = (data?.daily || []).map((d) => ({ date: d.date.slice(5), views: d.views, uniques: d.uniques }));
  const markerX = (data?.model_v2_since && chartData.some((d) => d.date === data.model_v2_since.slice(5))) ? data.model_v2_since.slice(5) : null;
  const noData = data && data.page_views === 0;
  const nvr = data ? [
    { label: "New Visitors", value: data.new_visitors || 0 },
    { label: "Returning Visitors", value: data.returning_visitors || 0 },
  ] : [];

  return (
    <div data-testid="traffic-tab">
      {/* controls */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex border border-sniper bg-surface p-1" data-testid="traffic-range-toggle">
            {RANGES.map(([v, l]) => (
              <button key={v} onClick={() => setRange(v)} data-testid={`range-${v}`}
                className={`px-4 py-2 font-mono uppercase text-xs tracking-wide transition-colors ${range === v ? "bg-olive text-white" : "text-secondary-foreground hover:text-white"}`}>
                {l}
              </button>
            ))}
          </div>
          {range === "custom" && (
            <div className="flex items-center gap-2" data-testid="custom-range">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="range-from"
                className="bg-input border border-sniper text-foreground font-mono text-xs px-2 py-2 focus:border-olive outline-none" />
              <span className="text-muted-foreground font-mono text-xs">→</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="range-to"
                className="bg-input border border-sniper text-foreground font-mono text-xs px-2 py-2 focus:border-olive outline-none" />
            </div>
          )}
          <button
            onClick={() => setIncludeStaff((v) => !v)}
            data-testid="toggle-include-staff"
            aria-pressed={includeStaff}
            title="Staff (admin / internal) sessions are excluded from traffic by default."
            className={`inline-flex items-center gap-2 px-3 py-2 font-mono uppercase text-xs tracking-wide border transition-colors ${
              includeStaff ? "border-olive-bright text-olive-bright bg-olive/10" : "border-sniper text-secondary-foreground hover:text-white"
            }`}
          >
            <span className={`inline-block w-3 h-3 border ${includeStaff ? "border-olive-bright bg-olive-bright" : "border-sniper"}`} />
            Include staff traffic
          </button>
        </div>
        <div className="flex items-center gap-3">
          {data && <CollectingNote since={data.collecting_since} />}
          <ExportButton path="/admin/analytics/export/sessions.csv" filename="opticswap_sessions.csv" testId="export-sessions" />
        </div>
      </div>

      {/* Active exclusions — stated on the tab, not left to the API response. Every panel below
          applies this same set. */}
      {data && (
        <div data-testid="active-exclusions" className="flex items-center flex-wrap gap-2 mb-5">
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Excluded from every panel:</span>
          {(data.active_exclusions || []).map((x) => (
            <span key={x} className="font-mono text-[10px] uppercase tracking-wide border border-sniper text-secondary-foreground px-2 py-0.5">{x}</span>
          ))}
          {data.include_staff && (
            <span className="font-mono text-[10px] uppercase tracking-wide border border-olive-bright/50 text-olive-bright px-2 py-0.5">staff included</span>
          )}
        </div>
      )}

      {/* Real-time */}
      <div className="border border-olive/50 bg-olive/5 p-4 mb-6" data-testid="realtime-panel">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-volt opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-volt" />
            </span>
            <span className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2"><Radio className="w-4 h-4 text-volt" /> Live — On Site Now</span>
          </div>
          <span className="font-heading text-4xl text-volt" data-testid="realtime-count">{rt?.active_count ?? 0}</span>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {(rt?.pages || []).length === 0 && <span className="font-mono text-xs text-muted-foreground">No visitors in the last 5 minutes.</span>}
          {(rt?.pages || []).map((p) => (
            <span key={p.path} className="font-mono text-[11px] border border-olive/40 text-olive-bright px-2 py-0.5">{p.path} · {p.count}</span>
          ))}
        </div>
      </div>

      {/* KPIs */}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="traffic-kpis">
            <CountTile icon={Eye} label="Page Views" value={data.page_views} />
            <CountTile icon={Activity} label="Sessions" value={data.sessions} />
            <CountTile icon={Users} label="Unique Visitors" value={data.unique_visitors} />
            <CountTile icon={UserPlus} label="Signups" value={data.signups} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <CountTile icon={Clock} label="Average Session Duration" value={secondsToDuration(data.avg_session_duration_sec)} />
            <CountTile icon={MousePointerClick} label="Bounce Rate" value={`${data.bounce_rate_pct}%`} />
            <CountTile icon={Repeat} label="Returning Visitors" value={data.returning_visitors} />
            <CountTile icon={PlusSquare} label="Listings Created" value={data.listings_created} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 mt-3" data-testid="traffic-quality">
            <StatNote label="Bot sessions excluded" value={data.sessions_excluded_bot}
              note="empty / unparseable / known-bot user-agent — kept in storage, hidden from tiles" />
            <StatNote label="Staff sessions excluded" value={data.include_staff ? "included" : data.sessions_excluded_staff}
              note={data.include_staff ? "staff traffic is currently included" : "admin / internal accounts + any session that touched an /admin route"} />
            <StatNote label="Seed / demo sessions excluded" value={data.sessions_excluded_seed ?? 0}
              note="sessions landing on seeded or demo entities (demo profiles, lst_seed / lst_demo listings)" />
            <StatNote label="Unique Visitors · stitched" value={`≈ ${data.unique_visitors_stitched}`}
              note={`raw ${data.unique_visitors_raw}${data.stitched_collapsed ? ` · ${data.stitched_collapsed} merged after sign-in` : ""}`} />
          </div>
          <p className="mt-2 text-[11px] font-mono text-muted-foreground leading-relaxed max-w-3xl" data-testid="unique-confidence-note">
            {data.unique_visitors_note}
          </p>
        </>
      )}

      {/* Daily dual-line chart */}
      <h2 className="font-heading text-2xl uppercase tracking-tight mt-10 mb-3 text-foreground">Visits &amp; Unique Visitors</h2>
      <div className="border border-sniper bg-surface p-4" data-testid="traffic-chart">
        {noData ? (
          <div className="h-[240px] flex flex-col items-center justify-center text-center" data-testid="traffic-no-data">
            <Activity className="w-8 h-8 text-muted-foreground" />
            <p className="mt-3 font-heading text-lg text-secondary-foreground">No traffic in this range yet.</p>
            {data.analytics_start_date && (
              <p className="text-xs font-mono uppercase text-muted-foreground mt-1">Analytics began {data.analytics_start_date}</p>
            )}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#2A2E2A" vertical={false} />
              <XAxis dataKey="date" stroke="#5C605C" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} interval={range === "30d" ? 4 : 0} />
              <YAxis stroke="#5C605C" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #2A2E2A", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11, textTransform: "uppercase" }} />
              <Line type="monotone" dataKey="views" name="Page Views" stroke="#CCFF00" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="uniques" name="Unique Visitors" stroke="#7C9E2D" strokeWidth={2} dot={false} />
              {markerX && <ReferenceLine x={markerX} stroke="#d3a24e" strokeDasharray="4 4" label={{ value: `model v2 · ${data.model_v2_since}`, fill: "#d3a24e", fontSize: 10, position: "insideTopRight" }} />}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* New vs Returning + Sources */}
      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        <div>
          <h2 className="font-heading text-2xl uppercase tracking-tight mb-3 text-foreground">New vs Returning</h2>
          <BarList rows={nvr} testId="new-returning" />
        </div>
        <div>
          <h2 className="font-heading text-2xl uppercase tracking-tight mb-1 text-foreground">Sources</h2>
          <p className="font-mono text-[10px] text-muted-foreground mb-3 leading-snug" data-testid="sources-note">
            {data?.sources_note || "One source per session — counts sum to sessions, not page views."}
          </p>
          <BarList testId="sources-table" rows={(data?.sources || []).map((s) => ({ label: s.source, value: s.sessions, pct: s.pct }))} />
          <p className="font-mono text-[10px] text-muted-foreground mt-2 leading-snug" data-testid="direct-note">
            <span className="text-secondary-foreground">{data?.direct_bucket_label || "Direct / unknown"}:</span> {data?.direct_note || "No referrer was sent (bookmarks, typed URLs, messaging apps, email clients) — missing attribution, not a measured channel."}
          </p>
        </div>
      </div>

      {/* Top Pages + Top Landing Pages */}
      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        <div>
          <h2 className="font-heading text-2xl uppercase tracking-tight mb-3 text-foreground">Top Pages</h2>
          <div className="border border-sniper bg-surface divide-y divide-sniper" data-testid="top-pages">
            {(data?.top_paths || []).length === 0 && <EmptyNote>No page views yet.</EmptyNote>}
            {(data?.top_paths || []).map((p) => (
              <div key={p.path} className="flex items-center justify-between p-2.5 font-mono text-sm">
                <span className="text-secondary-foreground truncate pr-3">{p.path}</span>
                <span className="text-foreground">{p.views}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-heading text-2xl uppercase tracking-tight mb-3 text-foreground">Top Landing Pages</h2>
          <div className="border border-sniper bg-surface divide-y divide-sniper" data-testid="top-landing-pages">
            {(data?.top_landing_pages || []).length === 0 && <EmptyNote>No sessions yet.</EmptyNote>}
            {(data?.top_landing_pages || []).map((p) => (
              <div key={p.path} className="flex items-center justify-between p-2.5 font-mono text-sm">
                <span className="text-secondary-foreground truncate pr-3">{p.path}</span>
                <span className="text-foreground">{p.sessions}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Site Search */}
      <SectionTitle sub={search ? `${search.total_searches} searches · ${search.unique_terms} unique terms · ${search.zero_result_total} zero-result` : undefined}>
        <span className="inline-flex items-center gap-2"><Search className="w-5 h-5 text-olive-bright" /> Site Search</span>
      </SectionTitle>
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <p className="font-mono text-[11px] uppercase text-muted-foreground mb-2">Top Search Terms</p>
          <BarList testId="search-top-terms" rows={(search?.top_terms || []).map((t) => ({ label: t.term, value: t.count }))} />
        </div>
        <div>
          <p className="font-mono text-[11px] uppercase text-blaze mb-2">Zero-Result Terms (unmet demand)</p>
          <div className="border border-blaze/40 bg-surface divide-y divide-sniper" data-testid="search-zero-results">
            {(search?.zero_result_terms || []).length === 0 && <EmptyNote>No zero-result searches yet.</EmptyNote>}
            {(search?.zero_result_terms || []).map((t) => (
              <div key={t.term} className="flex items-center justify-between p-2.5 font-mono text-sm">
                <span className="text-blaze truncate pr-3">{t.term}</span>
                <span className="text-foreground">{t.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Device / Browser */}
      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        <div>
          <h2 className="font-heading text-2xl uppercase tracking-tight mb-3 text-foreground flex items-center gap-2"><Monitor className="w-5 h-5 text-olive-bright" /> Devices</h2>
          <BarList testId="device-breakdown" rows={(data?.device_breakdown || []).map((d) => ({ label: d.label, value: d.sessions }))} />
        </div>
        <div>
          <h2 className="font-heading text-2xl uppercase tracking-tight mb-3 text-foreground">Browsers</h2>
          <BarList testId="browser-breakdown" rows={(data?.browser_breakdown || []).map((d) => ({ label: d.label, value: d.sessions }))} />
        </div>
      </div>

      {/* Visitor geo — gated */}
      <SectionTitle><span className="inline-flex items-center gap-2"><MapPin className="w-5 h-5 text-olive-bright" /> Visitor Geography</span></SectionTitle>
      <div className="border border-dashed border-sniper bg-surface p-8 text-center" data-testid="visitor-geo-gated">
        <MapPin className="w-8 h-8 text-muted-foreground mx-auto" />
        <p className="mt-3 font-heading text-lg text-secondary-foreground">Visitor location unavailable</p>
        <div className="mt-2 flex justify-center"><GatedPill note={data?.visitor_geo_note || "Requires IP geolocation — not wired"} /></div>
        <p className="text-xs font-mono uppercase text-muted-foreground mt-2">Registered-user geography is on the Marketplace tab.</p>
      </div>

      {/* Campaigns (UTM) */}
      <h2 className="font-heading text-2xl uppercase tracking-tight mt-10 mb-1 text-foreground flex items-center gap-2"><Megaphone className="w-5 h-5 text-olive-bright" /> Campaigns</h2>
      <p className="font-mono text-[10px] text-muted-foreground mb-3 leading-snug" data-testid="campaigns-note">
        {data?.campaigns_note || "Sessions that arrived with a utm_campaign parameter. Tag your links to move traffic out of Direct / unknown."}
      </p>
      {(data?.campaigns || []).length > 0 ? (
        <div className="border border-sniper bg-surface divide-y divide-sniper" data-testid="campaigns-table">
          <div className="flex items-center justify-between px-3 py-2 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            <span>Campaign · Source / Medium</span><span>Sessions</span>
          </div>
          {(data.campaigns || []).map((c, i) => (
            <div key={`${c.campaign}-${i}`} className="flex items-center justify-between p-2.5 font-mono text-sm" data-testid={`campaign-row-${i}`}>
              <span className="text-secondary-foreground truncate pr-3">
                {c.campaign} <span className="text-muted-foreground">· {c.source} / {c.medium}</span>
              </span>
              <span className="text-foreground">{c.sessions}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-dashed border-sniper bg-surface p-8 text-center" data-testid="paid-ads-panel">
          <Megaphone className="w-8 h-8 text-muted-foreground mx-auto" />
          <p className="mt-3 font-heading text-lg text-secondary-foreground">No tagged campaigns yet</p>
          <p className="text-xs font-mono uppercase text-muted-foreground mt-1">Add utm_source / utm_medium / utm_campaign to your links to see them here</p>
        </div>
      )}
      <div className="h-10" />
    </div>
  );
}
